import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

type FieldKey = 'firstName' | 'lastName' | 'email' | 'phone' | 'company' | 'street' | 'city' | 'state' | 'zip' | 'country'

interface FieldDef {
    key: FieldKey
    label: string
    type?: string
    kind?: 'select'
    placeholder: string
    fullWidth?: boolean
}

const FIELDS: FieldDef[] = [
    { key: 'firstName',  label: 'First Name',             placeholder: 'Jane' },
    { key: 'lastName',   label: 'Last Name',              placeholder: 'Smith' },
    { key: 'email',      label: 'Corporate Email Address', type: 'email', placeholder: 'jane.smith@company.com', fullWidth: true },
    { key: 'phone',      label: 'Phone Number',            type: 'tel',   placeholder: '+1 555-0199' },
    { key: 'company',    label: 'Company Name',            placeholder: 'Acme Corp', fullWidth: true },
    { key: 'street',     label: 'Street Address',          placeholder: '123 Main St', fullWidth: true },
    { key: 'city',       label: 'City',                    placeholder: 'San Jose' },
    { key: 'state',      label: 'State / Province',        placeholder: 'CA' },
    { key: 'zip',        label: 'Zip / Postal Code',       placeholder: '95101' },
    { key: 'country',    label: 'Country',                 kind: 'select', placeholder: 'Select a country', fullWidth: true },
]

interface Country { code: string; name: string }

function RequestFormPage() {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token') || ''

    const [countries, setCountries] = useState<Country[]>([])
    const [form, setForm] = useState<Record<FieldKey, string>>({
        firstName: '', lastName: '', email: '', phone: '', company: '',
        street: '', city: '', state: '', zip: '', country: '',
    })
    const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({})
    const [submitting, setSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [serverError, setServerError] = useState('')

    useEffect(() => {
        fetch('/api/event-request/countries')
            .then(r => r.json())
            .then(setCountries)
            .catch(() => { /* non-fatal, user can still type */ })
    }, [])

    if (!token) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full text-center">
                    <div className="inline-block p-4 bg-red-50 rounded-2xl mb-6">
                        <i className="iconoir-lock text-red-400 text-4xl"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">Access Not Permitted</h1>
                    <p className="text-gray-500 text-sm">
                        This form is only accessible via the event QR code.
                    </p>
                </div>
            </div>
        )
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
                <div className="max-w-md w-full text-center">
                    <div className="inline-block p-4 bg-yubico-green-50 rounded-2xl mb-6">
                        <i className="iconoir-check-circle text-yubico-green-400 text-4xl"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-3">Request Submitted</h1>
                    <p className="text-gray-600 text-sm leading-relaxed">
                        Thank you! Your YubiKey request has been received. You'll be notified once your shipment is approved and on its way.
                    </p>
                    <div className="mt-8 pt-6 border-t border-gray-100">
                        <p className="text-xs text-gray-400">Powered by Yubico FIDO Pre-Registration</p>
                    </div>
                </div>
            </div>
        )
    }

    function validate(): boolean {
        const next: Partial<Record<FieldKey, string>> = {}
        for (const { key, label, type } of FIELDS) {
            const value = form[key].trim()
            if (!value) {
                next[key] = `${label} is required`
            } else if (type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                next[key] = 'Enter a valid email address'
            }
        }
        setErrors(next)
        return Object.keys(next).length === 0
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault()
        if (!validate()) return

        setSubmitting(true)
        setServerError('')

        try {
            const res = await fetch('/api/event-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, ...form }),
            })
            if (res.ok) {
                setSubmitted(true)
            } else {
                const body = await res.json().catch(() => ({}))
                setServerError(body.error || 'Submission failed. Please try again.')
            }
        } catch {
            setServerError('Network error. Please check your connection and try again.')
        } finally {
            setSubmitting(false)
        }
    }

    const inputClass = (key: FieldKey) =>
        `w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-yubico-green-400 transition-all ${
            errors[key] ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
        }`

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
            <div className="max-w-lg w-full">

                <div className="text-center mb-8">
                    <div className="inline-block p-3 bg-yubico-green-50 rounded-2xl mb-4">
                        <i className="iconoir-key-alt-plus text-yubico-green-400 text-3xl"></i>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">YubiKey Request</h1>
                    <p className="text-gray-500 text-sm mt-2">
                        Fill out the form below to receive your YubiKey hardware security key.
                    </p>
                </div>

                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                    <form onSubmit={handleSubmit} noValidate>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            {FIELDS.map(({ key, label, type, kind, placeholder, fullWidth }) => (
                                <div key={key} className={fullWidth ? 'sm:col-span-2' : ''}>
                                    <label htmlFor={key} className="block text-sm font-semibold text-gray-700 mb-1.5">
                                        {label} <span className="text-red-400">*</span>
                                    </label>
                                    {kind === 'select' ? (
                                        <select
                                            id={key}
                                            value={form[key]}
                                            onChange={e => {
                                                setForm(f => ({ ...f, [key]: e.target.value }))
                                                if (errors[key]) setErrors(err => ({ ...err, [key]: undefined }))
                                            }}
                                            className={inputClass(key)}
                                        >
                                            <option value="">{placeholder}</option>
                                            {countries.map(c => (
                                                <option key={c.code} value={c.code}>{c.name}</option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            id={key}
                                            type={type || 'text'}
                                            value={form[key]}
                                            onChange={e => {
                                                setForm(f => ({ ...f, [key]: e.target.value }))
                                                if (errors[key]) setErrors(err => ({ ...err, [key]: undefined }))
                                            }}
                                            placeholder={placeholder}
                                            className={inputClass(key)}
                                        />
                                    )}
                                    {errors[key] && (
                                        <p className="mt-1 text-xs text-red-500">{errors[key]}</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {serverError && (
                            <div className="mt-5 p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">
                                {serverError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="mt-6 w-full bg-yubico-green-400 hover:bg-yubico-green-500 disabled:bg-gray-200 disabled:text-gray-400 text-white font-bold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2"
                        >
                            {submitting ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    <i className="iconoir-send text-base"></i>
                                    Submit Request
                                </>
                            )}
                        </button>
                    </form>
                </div>

                <p className="text-center text-xs text-gray-400 mt-6">
                    Powered by Yubico FIDO Pre-Registration &bull; ACME Corp
                </p>
            </div>
        </div>
    )
}

export default RequestFormPage
